/**
 * Company name formatting utilities
 *
 * Handles:
 * - Legal entity suffixes (LLC, Inc, Corp)
 * - Proper capitalization with special rules
 * - Acronyms and abbreviations
 */

import type { CompanyFormattingResult, LegalEntity } from './types';
import { normalizeWhitespace } from './text';

/**
 * Legal entity types and their variations
 */
const LEGAL_ENTITY_TYPES: Record<string, LegalEntity> = {
  // Corporation variations
  corporation: { type: 'Corporation', abbreviation: 'Corp', standardForm: 'Corporation' },
  incorporated: { type: 'Corporation', abbreviation: 'Inc', standardForm: 'Inc.' },
  corp: { type: 'Corporation', abbreviation: 'Corp', standardForm: 'Corp.' },
  'corp.': { type: 'Corporation', abbreviation: 'Corp', standardForm: 'Corp.' },
  inc: { type: 'Corporation', abbreviation: 'Inc', standardForm: 'Inc.' },
  'inc.': { type: 'Corporation', abbreviation: 'Inc', standardForm: 'Inc.' },

  // LLC variations
  llc: { type: 'LLC', abbreviation: 'LLC', standardForm: 'LLC' },
  'l.l.c': { type: 'LLC', abbreviation: 'LLC', standardForm: 'LLC' },
  'l.l.c.': { type: 'LLC', abbreviation: 'LLC', standardForm: 'LLC' },
  'limited liability company': { type: 'LLC', abbreviation: 'LLC', standardForm: 'LLC' },

  // Partnership variations
  llp: { type: 'Partnership', abbreviation: 'LLP', standardForm: 'LLP' },
  'l.l.p': { type: 'Partnership', abbreviation: 'LLP', standardForm: 'LLP' },
  'l.l.p.': { type: 'Partnership', abbreviation: 'LLP', standardForm: 'LLP' },
  'limited liability partnership': { type: 'Partnership', abbreviation: 'LLP', standardForm: 'LLP' },
  lp: { type: 'Partnership', abbreviation: 'LP', standardForm: 'LP' },
  'l.p': { type: 'Partnership', abbreviation: 'LP', standardForm: 'LP' },
  'l.p.': { type: 'Partnership', abbreviation: 'LP', standardForm: 'LP' },

  // Other
  company: { type: 'Company', abbreviation: 'Co', standardForm: 'Co.' },
  co: { type: 'Company', abbreviation: 'Co', standardForm: 'Co.' },
  'co.': { type: 'Company', abbreviation: 'Co', standardForm: 'Co.' },
  limited: { type: 'Limited', abbreviation: 'Ltd', standardForm: 'Ltd.' },
  ltd: { type: 'Limited', abbreviation: 'Ltd', standardForm: 'Ltd.' },
  'ltd.': { type: 'Limited', abbreviation: 'Ltd', standardForm: 'Ltd.' },
};

/**
 * Common company name patterns that should stay uppercase
 */
const ACRONYM_PATTERNS = [
  /^[A-Z]{2,}$/, // 2+ letter acronyms: IBM, HP, ABC
  /^[A-Z]\.([A-Z]\.)+/, // Dotted acronyms: U.S., A.B.C.
];

/**
 * Words that should remain lowercase in company names (unless at start)
 */
const LOWERCASE_WORDS = ['and', 'of', 'the', 'for', 'in', 'on', 'at', 'to'];

/**
 * Detect and standardize legal entity type
 *
 * @example
 * detectLegalEntity('Acme Inc') // { type: 'Corporation', abbreviation: 'Inc', standardForm: 'Inc.' }
 * detectLegalEntity('Acme LLC') // { type: 'LLC', abbreviation: 'LLC', standardForm: 'LLC' }
 */
export function detectLegalEntity(value: string): LegalEntity | null {
  if (!value) {
    return null;
  }

  // Normalize and get last 1-3 words (where entity types usually are)
  const normalized = normalizeWhitespace(value).formatted;
  const words = normalized.split(' ');

  // Check longer phrases first (most specific to least specific)

  // Check last 3 words (e.g., "Limited Liability Company")
  if (words.length >= 4) { // At least 4 words total (company name + 3-word entity)
    const lastThree = words.slice(-3).join(' ').toLowerCase().replace(/\./g, '');
    if (LEGAL_ENTITY_TYPES[lastThree]) {
      return LEGAL_ENTITY_TYPES[lastThree];
    }
  }

  // Check last 2 words
  if (words.length >= 3) { // At least 3 words total (company name + 2-word entity)
    const lastTwo = words.slice(-2).join(' ').toLowerCase().replace(/\./g, '');
    if (LEGAL_ENTITY_TYPES[lastTwo]) {
      return LEGAL_ENTITY_TYPES[lastTwo];
    }
  }

  // Check last word (but NOT if it's "company" and it's the only word or part of the actual name)
  if (words.length >= 2) { // At least 2 words total
    const lastWord = words[words.length - 1].toLowerCase().replace(/\./g, '');

    // Don't treat standalone "company" as an entity suffix
    if (lastWord === 'company') {
      return null;
    }

    if (LEGAL_ENTITY_TYPES[lastWord]) {
      return LEGAL_ENTITY_TYPES[lastWord];
    }
  }

  return null;
}

/**
 * Check if a word is an acronym (should stay uppercase)
 */
function isAcronym(word: string): boolean {
  return ACRONYM_PATTERNS.some((pattern) => pattern.test(word));
}

/**
 * Format a company name
 * Handles capitalization, entity types, and special characters
 *
 * @example
 * formatCompanyName('acme corporation')
 * // { formatted: 'Acme Corporation', legalType: 'Corporation' }
 *
 * formatCompanyName('abc llc')
 * // { formatted: 'ABC LLC', legalType: 'LLC' }
 *
 * formatCompanyName('smiths heating & cooling')
 * // { formatted: "Smith's Heating & Cooling" }
 */
export function formatCompanyName(value: string): CompanyFormattingResult {
  if (!value) {
    return { formatted: '' };
  }

  // Normalize whitespace
  const normalized = normalizeWhitespace(value).formatted;

  // Detect legal entity
  const legalEntity = detectLegalEntity(normalized);

  // Calculate how many words are part of the entity suffix
  let entityWordCount = 0;
  if (legalEntity) {
    const words = normalized.split(' ');
    // Check if last 3 words match
    if (words.length >= 3) {
      const lastThree = words.slice(-3).join(' ').toLowerCase().replace(/\./g, '');
      if (LEGAL_ENTITY_TYPES[lastThree]) {
        entityWordCount = 3;
      }
    }
    // Check if last 2 words match
    if (entityWordCount === 0 && words.length >= 2) {
      const lastTwo = words.slice(-2).join(' ').toLowerCase().replace(/\./g, '');
      if (LEGAL_ENTITY_TYPES[lastTwo]) {
        entityWordCount = 2;
      }
    }
    // Check if last word matches
    if (entityWordCount === 0 && words.length >= 2) {
      const lastWord = words[words.length - 1].toLowerCase().replace(/\./g, '');
      if (LEGAL_ENTITY_TYPES[lastWord] && lastWord !== 'company') {
        entityWordCount = 1;
      }
    }
  }

  // Split into words for processing
  const words = normalized.split(' ');

  // Format each word
  const formattedWords = words.map((word, index) => {
    const lower = word.toLowerCase();

    // Check if this word is part of the legal entity suffix
    const isEntityWord = entityWordCount > 0 && index >= words.length - entityWordCount;
    if (isEntityWord) {
      // Will be replaced with standardForm later
      return word;
    }

    // Treat 2-3 letter words as likely acronyms (HP, IBM, ABC)
    // unless they're known minor words
    const isLikelyAcronym =
      word.length >= 2 &&
      word.length <= 3 &&
      !LOWERCASE_WORDS.includes(lower);

    if (isLikelyAcronym) {
      return word.toUpperCase();
    }

    // Check if word is a dotted acronym (U.S., A.B.C.)
    if (isAcronym(word) && word.includes('.')) {
      return word.toUpperCase();
    }

    // Keep minor words lowercase (unless first word)
    if (index > 0 && LOWERCASE_WORDS.includes(lower)) {
      return lower;
    }

    // Keep ampersand as-is
    if (word === '&') {
      return '&';
    }

    // Default: capitalize first letter, lowercase rest
    // This handles both normal words and all-caps input (4+ letters)
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  // Replace entity suffix with standard form
  if (legalEntity && entityWordCount > 0) {
    // Remove entity words and add standard form
    formattedWords.splice(-entityWordCount, entityWordCount, legalEntity.standardForm);
  }

  const formatted = formattedWords.join(' ');

  // Build standardized version if entity type was detected
  let standardized: string | undefined;
  if (legalEntity && entityWordCount > 0) {
    // Check if original entity suffix is the same as standard form (case-insensitive)
    const originalEntitySuffix = words.slice(-entityWordCount).join(' ');
    const isAlreadyStandardized =
      originalEntitySuffix.toLowerCase() === legalEntity.standardForm.toLowerCase();

    // Get company name part (without entity)
    const formattedNameWords = formattedWords.slice(0, -1); // Exclude the standardForm we just added

    // Build standardized version
    const standardizedValue = `${formattedNameWords.join(' ')} ${legalEntity.standardForm}`;

    // Only return standardized if:
    // 1. Original entity suffix differs from standard form (case-insensitive), OR
    // 2. The standardized value differs from formatted value
    if (!isAlreadyStandardized || standardizedValue !== formatted) {
      standardized = standardizedValue;
    }
  }

  return {
    formatted,
    standardized,
    legalType: legalEntity?.type,
  };
}
